import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Product } from '../types';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';

const Products: React.FC = () => {
  const { products, categories: dataCategories } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const [selectedCategory, setSelectedCategory] = useState<string>(categoryParam || 'All');
  
  useEffect(() => {
    // Update selected category when URL changes
    setSelectedCategory(categoryParam || 'All');
  }, [categoryParam]);

  useEffect(() => {
    // Scroll to top when component mounts or category changes
    window.scrollTo(0, 0);
  }, [categoryParam]);
  
  // Filter out inactive products
  const activeProducts = products.filter(p => p.active !== false);
  
  // Use categories from DataContext and filter out inactive ones
  const activeCategories = dataCategories.filter(c => c.active !== false);
  const categoryNames = ['All', ...activeCategories.map(c => c.name)];
  
  
  const filteredProducts = selectedCategory === 'All' 
    ? [...activeProducts].sort((a, b) => {
        if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
          return a.displayOrder - b.displayOrder;
        }
        if (a.displayOrder !== undefined) return -1;
        if (b.displayOrder !== undefined) return 1;
        return a.name.localeCompare(b.name);
      })
    : activeProducts.filter(p => p.category === selectedCategory).sort((a, b) => {
        if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
          return a.displayOrder - b.displayOrder;
        }
        if (a.displayOrder !== undefined) return -1;
        if (b.displayOrder !== undefined) return 1;
        return a.name.localeCompare(b.name);
      });

  return (
    <div className="container mx-auto px-4 py-8">
      <SEO 
        title={selectedCategory === 'All' ? 'Products' : `${selectedCategory} Products`}
        description={`Browse our ${selectedCategory === 'All' ? 'complete selection of' : selectedCategory} signs, banners, and printing products. Custom sizes available. Fast turnaround in Montgomery County.`}
        keywords={`${selectedCategory} printing, custom ${selectedCategory}, ${selectedCategory} signs, Montgomery County printing`}
      />
      <Breadcrumb 
        items={[
          { label: 'Products' }
        ]}
      />
      <h1 className="text-4xl font-bold mb-8">Our Products</h1>
      
      <div className="mb-8">
        <div className="flex flex-wrap gap-2">
          {categoryNames.map((categoryName, index) => (
            <button
              key={`category-${index}-${categoryName}`}
              onClick={() => {
                setSelectedCategory(categoryName);
                if (categoryName === 'All') {
                  // Remove category param when "All" is selected
                  searchParams.delete('category');
                  setSearchParams(searchParams);
                } else {
                  // Set category param for specific categories
                  setSearchParams({ ...Object.fromEntries(searchParams), category: categoryName });
                }
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === categoryName
                  ? 'bg-keystone-blue text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {categoryName}
            </button>
          ))}
        </div>
      </div>

      {filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">
            {selectedCategory === 'All' 
              ? 'No products available at the moment.' 
              : `No products found in the "${selectedCategory}" category.`}
          </p>
          {selectedCategory !== 'All' && (
            <p className="text-gray-500 mt-2">
              Products need to be assigned to this category in the admin panel.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Generate URL-friendly slug from product name
  const productSlug = product.name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
  
  // Combine primary image with additional images
  const images = [];
  if (product.image) {
    images.push(product.image);
  }
  if (product.images && product.images.length > 0) {
    images.push(...product.images);
  }
  
  const handlePrevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };
  
  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };
  
  const navigate = useNavigate();
  
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    navigate(`/products/${product.id}/${productSlug}`);
  };
  
  return (
    <div 
      onClick={handleCardClick}
      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow flex flex-col h-full cursor-pointer"
    >
      <div className="aspect-square bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 relative group">
        {images.length > 0 ? (
          <>
            <div className="relative w-full h-full">
              {images.map((image, index) => (
                <img 
                  key={index}
                  src={image} 
                  alt={`${product.name} - Image ${index + 1}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                    index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
            </div>
            {images.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  aria-label="Previous image"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  aria-label="Next image"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentImageIndex(index);
                      }}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <span className="text-gray-500">No Image</span>
        )}
      </div>
      <div className="p-6 flex flex-col flex-grow">
        <h3 className="text-xl font-semibold mb-2">
          <Link 
            to={`/products/${product.id}/${productSlug}`}
            className="hover:text-keystone-blue transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {product.name}
          </Link>
        </h3>
        <p className="text-gray-600 mb-4 flex-grow whitespace-pre-line">{product.description}</p>
        
        {/* This div will be pushed to the bottom */}
        <div className="mt-auto">
          <div className="mb-4">
            <span className="text-lg font-medium">
              {!product.customizable && product.fixedSizes && product.fixedSizes.length > 0 ? (
                <>Starting at ${Math.min(...product.fixedSizes.map(size => size.price)).toFixed(2)}</>
              ) : (
                product.minWidth && product.minHeight && (
                  <>Starting at ${(product.minWidth * product.minHeight / 144 * product.basePrice).toFixed(2)}</>
                )
              )}
            </span>
          </div>
          {product.customizable ? (
            <p className="text-sm text-gray-500 mb-4">
              Size: {product.minWidth! % 12 === 0 ? (product.minWidth! / 12) : (product.minWidth! / 12).toFixed(1)}ft × {product.minHeight! % 12 === 0 ? (product.minHeight! / 12) : (product.minHeight! / 12).toFixed(1)}ft to {product.maxWidth! % 12 === 0 ? (product.maxWidth! / 12) : (product.maxWidth! / 12).toFixed(1)}ft × {product.maxHeight! % 12 === 0 ? (product.maxHeight! / 12) : (product.maxHeight! / 12).toFixed(1)}ft
            </p>
          ) : (
            product.fixedSizes && product.fixedSizes.length > 0 && (
              <p className="text-sm text-gray-500 mb-4">
                {product.fixedSizes.length === 1 ? (
                  <>Size: {product.fixedSizes[0].width}" × {product.fixedSizes[0].height}"{product.fixedSizes[0].note ? ` (${product.fixedSizes[0].note})` : ''}</>
                ) : (
                  <>Available in {product.fixedSizes.length} sizes</>
                )}
              </p>
            )
          )}
          <Link
            to={`/quote?product=${product.id}`}
            className="block w-full text-center bg-keystone-gold text-keystone-blue font-semibold py-2 rounded-lg hover:bg-keystone-gold-600 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            Get Quote
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Products;